/**
 * Auth overlay motion presets for `AuthSheetHost`.
 * Types mirror `ConfigurableOverlay` in each UI version tree (shared code cannot import versioned components).
 */
import type { AuthSheetState } from './authSheetContext';

export type ConfigurableOverlayPlacement = 'bottom' | 'center';
export type ConfigurableOverlayEnterPreset = 'slideUp' | 'fade' | 'scale' | 'scaleOut';

const DEFAULT_HIDDEN_SLIDE = 420;

/** Max width for the centered inline auth card on `MainScreen` (panel `width: min(88%, this)`). */
export const MAIN_SCREEN_PANEL_MAX_WIDTH = 720;

export type AuthOverlayMotion = {
  placement: ConfigurableOverlayPlacement;
  widthFraction: number;
  panelMaxWidth?: number;
  maxHeightFraction?: number;
  enterPreset: ConfigurableOverlayEnterPreset;
  exitPreset: ConfigurableOverlayEnterPreset;
  openDurationMs: number;
  closeDurationMs: number;
  hiddenSlideOffset?: number;
  shrinkWrapPanel?: boolean;
  backdropOpacityMax?: number;
  showHandle?: boolean;
  panelBorderRadius?: number;
};

/** Default = set1 / shared host sheets: centered shrink-wrap card (docs/old auth flow). */
const DEFAULT_MOTION: AuthOverlayMotion = {
  placement: 'center',
  widthFraction: 0.9,
  panelMaxWidth: 420,
  maxHeightFraction: 0.86,
  /** Fade — no linear slide thrash, no scaleY squash; keyboard lift is separate. */
  enterPreset: 'scale',
  exitPreset: 'scaleOut',
  openDurationMs: 240,
  closeDurationMs: 200,
  shrinkWrapPanel: true,
  backdropOpacityMax: 0.55,
  showHandle: false,
  panelBorderRadius: 24,
};

/** hero_dock `presentation: 'mainScreen'` — keep bottom/slide path for dock host. */
const MAIN_SCREEN_MOTION: AuthOverlayMotion = {
  placement: 'bottom',
  widthFraction: 1,
  enterPreset: 'slideUp',
  exitPreset: 'slideUp',
  openDurationMs: 280,
  closeDurationMs: 240,
  hiddenSlideOffset: DEFAULT_HIDDEN_SLIDE,
  shrinkWrapPanel: true,
  backdropOpacityMax: 1,
  showHandle: true,
};

function presentationOf(s: NonNullable<AuthSheetState>): string {
  if (s.kind === 'login' || s.kind === 'register') {
    return s.presentation ?? 'default';
  }
  return 'default';
}

export function getAuthOverlayMotionForState(state: AuthSheetState | null): AuthOverlayMotion {
  if (state != null && getAuthSheetPresentation(state) === 'mainScreen') {
    return MAIN_SCREEN_MOTION;
  }
  if (state?.kind === 'otp' || state?.kind === 'reset') {
    return {
      ...DEFAULT_MOTION,
      enterPreset: 'fade',
      exitPreset: 'fade',
      openDurationMs: 220,
      closeDurationMs: 200,
    };
  }
  return DEFAULT_MOTION;
}

/** `AuthSheetHost` / callers — which overlay layout to use. */
export function getAuthSheetPresentation(s: AuthSheetState | null): 'default' | 'mainScreen' {
  if (s == null) {
    return 'default';
  }
  const p = presentationOf(s);
  return p === 'mainScreen' ? 'mainScreen' : 'default';
}

/** Shallow compare — avoids `JSON.stringify` on the JS thread during auth state checks. */
function recordShallowEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  if (a === b) {
    return true;
  }
  const keys_a = Object.keys(a);
  if (keys_a.length !== Object.keys(b).length) {
    return false;
  }
  for (let i = 0; i < keys_a.length; i++) {
    const k = keys_a[i];
    if (!Object.prototype.hasOwnProperty.call(b, k) || a[k] !== b[k]) {
      return false;
    }
  }
  return true;
}

function subDataEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
    return false;
  }
  return recordShallowEqual(a as Record<string, unknown>, b as Record<string, unknown>);
}

export function authSheetStateEqual(a: AuthSheetState | null, b: AuthSheetState | null): boolean {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (a.kind !== b.kind) {
    return false;
  }
  if (a.kind === 'login' && b.kind === 'login') {
    return presentationOf(a) === presentationOf(b);
  }
  if (a.kind === 'register' && b.kind === 'register') {
    return presentationOf(a) === presentationOf(b) && subDataEqual(a.subData, b.subData);
  }
  if (a.kind === 'otp' && b.kind === 'otp') {
    return recordShallowEqual(a.params, b.params);
  }
  if (a.kind === 'reset' && b.kind === 'reset') {
    return recordShallowEqual(a.params, b.params);
  }
  return false;
}

/**
 * When true, `AuthSheetHost` closes the overlay (`setOverlayVisible(false)`), then on
 * `onCloseAnimationComplete` applies the pending state and reopens — full exit + enter on `ConfigurableOverlay`.
 * Login ↔ register (any presentation) returns **false** so the overlay stays open and only the body swaps.
 */
export function authSheetRequiresSequencedTransition(from: AuthSheetState | null, to: AuthSheetState | null): boolean {
  if (from == null || to == null) {
    return false;
  }
  const from_cred = from.kind === 'login' || from.kind === 'register';
  const to_cred = to.kind === 'login' || to.kind === 'register';
  if (from_cred && to_cred) {
    return false;
  }
  if (from.kind !== to.kind) {
    return true;
  }
  if (from.kind === 'login' && to.kind === 'login') {
    return presentationOf(from) !== presentationOf(to);
  }
  if (from.kind === 'register' && to.kind === 'register') {
    return presentationOf(from) !== presentationOf(to) || !subDataEqual(from.subData, to.subData);
  }
  if (from.kind === 'otp' && to.kind === 'otp') {
    return !recordShallowEqual(from.params, to.params);
  }
  if (from.kind === 'reset' && to.kind === 'reset') {
    return !recordShallowEqual(from.params, to.params);
  }
  return false;
}
